/**
 * Zustand stores — the in-memory state shape, with Dexie persistence.
 *
 * The cards store mirrors FSRS card state. Writes are debounced so we don't
 * hammer IndexedDB on every keystroke.
 */

import { create } from "zustand";
import type { Card, FsrsParams, MasteryPreset, Rating, Track } from "@music-app/learning-system";
import { DEFAULT_PARAMS, applyReview as applyFsrsReview, getItemTrack } from "@music-app/learning-system";
import { loadAllCards, saveCard, getPreference, setPreference, loadUserPlans, savePlan, deletePlan, type SavedPlanRecord } from "./repository.js";

interface CardsState {
  cards: Record<string, Card>;
  loaded: boolean;
  /** Hydrate from IndexedDB on app boot. Call once. */
  hydrate: () => Promise<void>;
  /** Apply a review and persist the updated card. */
  applyReview: (itemId: string, rating: Rating, now?: Date) => Promise<void>;
  /** Replace the entire cards map (for import). */
  replaceAll: (cards: Record<string, Card>) => void;
  /**
   * Seed a card from a first-correct MiniExercise answer.
   * Sets stability:3, difficulty:6, state:"learning" (due immediately).
   * No-op if the card already has stability > 0 — never downgrades.
   */
  seedFromLesson: (itemId: string, now?: Date) => Promise<void>;
}

export const useCardsStore = create<CardsState>((set, get) => ({
  cards: {},
  loaded: false,

  hydrate: async () => {
    // No `loaded` early-return: this is also called after import to pick
    // up the freshly-written rows, and the early gate would silently skip
    // that re-read. The DB hit is cheap enough to justify the simpler
    // semantics.
    const cards = await loadAllCards();
    set({ cards, loaded: true });
  },

  applyReview: async (itemId, rating, now = new Date()) => {
    const cards = get().cards;
    const { fsrsWeights, fsrsWeightsByTrack, targetRetention } = usePrefsStore.getState();
    const track = getItemTrack(itemId);
    const trackWeights = track ? (fsrsWeightsByTrack?.[track] ?? null) : null;
    const weights = trackWeights ?? fsrsWeights ?? null;
    const params: FsrsParams = {
      ...DEFAULT_PARAMS,
      ...(weights ? { w: weights } : {}),
      requestRetention: targetRetention ?? 0.9,
    };
    const updated = applyFsrsReview(cards, itemId, rating, now, params);
    set({ cards: updated });
    // Persist the single updated card immediately. (Bulk ops use saveCards directly.)
    const newCard = updated[itemId];
    if (newCard) {
      await saveCard(itemId, newCard);
    }
  },

  replaceAll: (cards) => {
    set({ cards });
  },

  seedFromLesson: async (itemId, now = new Date()) => {
    const existing = get().cards[itemId];
    if (existing && existing.stability > 0) return;
    const seeded: Card = {
      stability: 3, difficulty: 6, reps: 1, lapses: 0,
      state: "learning",
      lastReview: now.toISOString(),
      due: now.toISOString(),
    };
    set({ cards: { ...get().cards, [itemId]: seeded } });
    await saveCard(itemId, seeded);
  },
}));

/* ─── Preferences store ──────────────────────────────────────────────── */

interface PrefsState {
  instrument: "synth" | "piano" | "guitar" | "voice";
  showLetterNames: boolean;
  includeLeadingTone: boolean;
  /** True once the user has dismissed the first-visit welcome card. */
  onboardingComplete: boolean;
  /** Whether short non-musical confirmation tones play after each grade. */
  uiSoundsEnabled: boolean;
  /**
   * Bypass curriculum prerequisite gates so every node is reachable.
   * MVP-only as a visible toggle in Settings; will be hidden behind a
   * URL-hash trigger before public launch. Card state and progress are
   * untouched — this only affects what's *visible* to drill.
   */
  unlockEverything: boolean;
  /**
   * True once the user has completed (or explicitly skipped) the
   * placement test. Drives the first-run redirect from Home: a fresh
   * install with no cards and no completion sees a placement CTA.
   */
  placementCompleted: boolean;
  /**
   * Seed used by `buildPlacementSession` so retakes can vary the items
   * shown. Default 0 for the first run; Settings → Reset placement
   * bumps this to a new value before navigating to /placement.
   */
  placementSeed: number;
  /**
   * True once the user has completed (or skipped) the dedicated guitar
   * placement. Independent of `placementCompleted` — a guitarist can
   * baseline the guitar curriculum separately. Drives the Guitar Hub
   * placement banner.
   */
  guitarPlacementCompleted: boolean;
  /** Seed for the guitar placement pool; bumped on retake. */
  guitarPlacementSeed: number;
  /**
   * Curriculum nodes for which the user has answered every `checkFor`
   * MiniExercise correctly at least once during a lesson view. Drives
   * the "completed" state in the /learn index. Stored as a sorted
   * string array (Set serializes poorly via JSON / Dexie).
   */
  lessonsCompleted: string[];
  /**
   * Vocal mode (Phase 2A C.1). When true, scale-degree exercises in
   * Practice surface a "Sing the answer" button that uses the
   * @music-app/input-mic detector to grade sung pitches. Default false
   * — the toggle is opt-in per spec §18 (mic permission UX is the
   * adoption killer; show value before asking for permission).
   * The UI never prompts for mic permission unless this is set.
   */
  vocalModeEnabled: boolean;
  /**
   * MIDI input mode (Phase 2A C.2). When true, supported exercise types
   * in Practice (scale-degree, interval, chord-quality, chord-inversion)
   * accept noteon events from a connected MIDI keyboard and auto-submit.
   * Same opt-in posture as vocal mode — Earwise never requests MIDI
   * permission unless this is set, and clicking is always available
   * even when this is on (MIDI is additive, not exclusive).
   */
  midiInputEnabled: boolean;
  /**
   * Selected MIDI device id, or null to listen to every detected
   * device. The id is the stable Web MIDI MIDIInput.id; settings
   * persists this so the user's device pick survives reconnects /
   * reloads. Settings populates the picker live via the MidiInput
   * `onDevicesChanged` callback.
   */
  midiSelectedDeviceId: string | null;
  /**
   * Target session length in minutes. Passed as `config.targetMinutes` to
   * `buildSession`. Defaults to 20 (the DEFAULT_SESSION_CONFIG value).
   */
  sessionTargetMinutes: number;
  /**
   * Session scheduling mix preset. Controls how items are drawn from the
   * three buckets (due reviews / focus / new material).
   *   "review-only"  → 80% review, 20% focus, 0% new
   *   "balanced"     → 40% review, 40% focus, 20% new (default)
   *   "new-focus"    → 20% review, 40% focus, 40% new
   */
  sessionMix: "review-only" | "balanced" | "new-focus";
  /**
   * Which curriculum node to draw new items from. When set, `buildSession`
   * restricts its new-item pool to this node (falls back to auto if the
   * node has no remaining unseen items). Null = auto, draw from the full
   * pedagogically-eligible frontier.
   */
  newSkillNodeId: string | null;
  /**
   * User-optimized FSRS-4.5 weights (Block G). Null → use DEFAULT_PARAMS.
   * Stored as a plain number array in preferences so it survives export/import.
   */
  fsrsWeights: number[] | null;
  /**
   * Per-track FSRS weights (Phase 2D Feature 4). Overrides `fsrsWeights` for
   * items on that track; falls back to `fsrsWeights` then DEFAULT_PARAMS.
   * Null means no per-track overrides are active.
   */
  fsrsWeightsByTrack: Partial<Record<Track, number[]>> | null;
  /**
   * Which mastery-threshold preset the user has chosen. Controls what stability
   * (in days) a card needs before its node shows as "practicing" or "mastered"
   * in the Skill Tree, Stats, and Learn pages.
   */
  masteryPreset: MasteryPreset;
  cadenceTempo: "slow" | "medium" | "fast";
  /**
   * FSRS target retention probability (Phase 2C). Controls how frequently
   * cards are scheduled: higher values → shorter intervals (more reviews,
   * near-perfect retention); lower values → longer intervals (fewer reviews,
   * some forgetting). Default 0.9. Exposed in Settings → Schedule.
   */
  targetRetention: number;
  /**
   * When true, the Practice graded state shows a one-line hint with the
   * updated card's next-review date and stability. Off by default so it
   * doesn't overwhelm new users. Toggled in Settings → Display.
   */
  showNextReviewHint: boolean;
  /** Ask "Got it / Wasn't sure?" after correct answers so FSRS gets a
   *  confidence signal instead of relying solely on response time.
   *  Off by default. Exposed in Settings → Exercises. */
  confidenceSelfReport: boolean;
  /** Auto-advance to the next exercise in listen mode after a short delay.
   *  For hands-busy use. Off by default. Exposed in Settings → Exercises and
   *  inline on the listen screen. */
  listenAutoAdvance: boolean;
  /** Instrument-specific tracks the user has opted into (e.g. "guitar").
   *  Defaults to ["guitar"] for new users; gates the guitar curriculum via
   *  visibleNodes/visibleItems. Users can turn it off in Settings.
   *  See docs/design/guitar-harmony-v1.md §5.1. */
  enabledInstrumentTracks: Track[];
  activeSavedPlanId: string;
  loaded: boolean;
  hydrate: () => Promise<void>;
  setInstrument: (instrument: PrefsState["instrument"]) => Promise<void>;
  setShowLetterNames: (show: boolean) => Promise<void>;
  setIncludeLeadingTone: (include: boolean) => Promise<void>;
  setOnboardingComplete: (done: boolean) => Promise<void>;
  setUISoundsEnabled: (enabled: boolean) => Promise<void>;
  setUnlockEverything: (unlock: boolean) => Promise<void>;
  setPlacementCompleted: (done: boolean) => Promise<void>;
  setPlacementSeed: (seed: number) => Promise<void>;
  setGuitarPlacementCompleted: (done: boolean) => Promise<void>;
  setGuitarPlacementSeed: (seed: number) => Promise<void>;
  /**
   * Idempotently mark a lesson complete. No-op when the nodeId is
   * already in the set, so callers can fire on every successful answer
   * without worrying about double-writes.
   */
  markLessonComplete: (nodeId: string) => Promise<void>;
  /** Synchronous lookup, reads from the in-memory state. */
  isLessonCompleted: (nodeId: string) => boolean;
  setVocalModeEnabled: (enabled: boolean) => Promise<void>;
  setMidiInputEnabled: (enabled: boolean) => Promise<void>;
  setMidiSelectedDeviceId: (id: string | null) => Promise<void>;
  setSessionTargetMinutes: (minutes: number) => Promise<void>;
  setSessionMix: (mix: "review-only" | "balanced" | "new-focus") => Promise<void>;
  setNewSkillNodeId: (id: string | null) => Promise<void>;
  setFsrsWeights: (weights: number[] | null) => Promise<void>;
  setFsrsWeightsByTrack: (weights: Partial<Record<Track, number[]>> | null) => Promise<void>;
  setMasteryPreset: (preset: MasteryPreset) => Promise<void>;
  setCadenceTempo: (tempo: "slow" | "medium" | "fast") => Promise<void>;
  setTargetRetention: (retention: number) => Promise<void>;
  setShowNextReviewHint: (show: boolean) => Promise<void>;
  setConfidenceSelfReport: (enabled: boolean) => Promise<void>;
  setListenAutoAdvance: (enabled: boolean) => Promise<void>;
  setEnabledInstrumentTracks: (tracks: Track[]) => Promise<void>;
  setActiveSavedPlanId: (id: string) => Promise<void>;
}

export const usePrefsStore = create<PrefsState>((set, get) => ({
  instrument: "piano",
  showLetterNames: false,
  includeLeadingTone: true,
  onboardingComplete: false,
  uiSoundsEnabled: true,
  unlockEverything: false,
  placementCompleted: false,
  placementSeed: 0,
  guitarPlacementCompleted: false,
  guitarPlacementSeed: 0,
  lessonsCompleted: [],
  vocalModeEnabled: false,
  midiInputEnabled: false,
  midiSelectedDeviceId: null,
  sessionTargetMinutes: 20,
  sessionMix: "balanced",
  newSkillNodeId: null,
  fsrsWeights: null,
  fsrsWeightsByTrack: null,
  masteryPreset: "standard",
  cadenceTempo: "slow",
  targetRetention: 0.9,
  showNextReviewHint: false,
  confidenceSelfReport: false,
  listenAutoAdvance: false,
  enabledInstrumentTracks: ["guitar"],
  activeSavedPlanId: "app-standard",
  loaded: false,

  hydrate: async () => {
    const [
      instrument,
      showLetterNames,
      includeLeadingTone,
      onboardingComplete,
      uiSoundsEnabled,
      unlockEverything,
      placementCompleted,
      placementSeed,
      guitarPlacementCompleted,
      guitarPlacementSeed,
      lessonsCompleted,
      vocalModeEnabled,
      midiInputEnabled,
      midiSelectedDeviceId,
      sessionTargetMinutes,
      sessionMix,
      newSkillNodeId,
      fsrsWeights,
      fsrsWeightsByTrack,
      masteryPreset,
      cadenceTempo,
      targetRetention,
      showNextReviewHint,
      confidenceSelfReport,
      listenAutoAdvance,
      activePlanTemplateId,
      enabledInstrumentTracks,
      activeSavedPlanId_raw,
    ] = await Promise.all([
      getPreference<PrefsState["instrument"]>("instrument", "piano"),
      getPreference<boolean>("showLetterNames", false),
      getPreference<boolean>("includeLeadingTone", true),
      getPreference<boolean>("onboardingComplete", false),
      getPreference<boolean>("uiSoundsEnabled", true),
      getPreference<boolean>("unlockEverything", false),
      getPreference<boolean>("placementCompleted", false),
      getPreference<number>("placementSeed", 0),
      getPreference<boolean>("guitarPlacementCompleted", false),
      getPreference<number>("guitarPlacementSeed", 0),
      getPreference<string[]>("lessonsCompleted", []),
      getPreference<boolean>("vocalModeEnabled", false),
      getPreference<boolean>("midiInputEnabled", false),
      getPreference<string | null>("midiSelectedDeviceId", null),
      getPreference<number>("sessionTargetMinutes", 20),
      getPreference<"review-only" | "balanced" | "new-focus">("sessionMix", "balanced"),
      getPreference<string | null>("newSkillNodeId", null),
      getPreference<number[] | null>("fsrsWeights", null),
      getPreference<Partial<Record<Track, number[]>> | null>("fsrsWeightsByTrack", null),
      getPreference<MasteryPreset>("masteryPreset", "standard"),
      getPreference<"slow" | "medium" | "fast">("cadenceTempo", "slow"),
      getPreference<number>("targetRetention", 0.9),
      getPreference<boolean>("showNextReviewHint", false),
      getPreference<boolean>("confidenceSelfReport", false),
      getPreference<boolean>("listenAutoAdvance", false),
      getPreference<string>("activePlanTemplateId", "standard"),
      getPreference<Track[]>("enabledInstrumentTracks", ["guitar"]),
      getPreference<string | null>("activeSavedPlanId", null),
    ]);

    // Migrate from legacy activePlanTemplateId → activeSavedPlanId
    const LEGACY_MAP: Record<string, string> = {
      "standard": "app-standard",
      "intensive": "app-intensive",
      "review-sprint": "app-review-sprint",
      "harmony-focus": "app-harmony-focus",
      "pitch-and-modes": "app-pitch-and-modes",
    };
    let activeSavedPlanId: string;
    if (typeof activeSavedPlanId_raw === "string" && activeSavedPlanId_raw.length > 0) {
      activeSavedPlanId = activeSavedPlanId_raw;
    } else {
      activeSavedPlanId = LEGACY_MAP[activePlanTemplateId] ?? "app-standard";
      // Write the migrated value immediately
      void setPreference("activeSavedPlanId", activeSavedPlanId);
    }
    set({
      instrument,
      showLetterNames,
      includeLeadingTone,
      onboardingComplete,
      uiSoundsEnabled,
      unlockEverything,
      placementCompleted,
      placementSeed,
      guitarPlacementCompleted,
      guitarPlacementSeed,
      // Older exports may have stored a non-array (or missing); coerce.
      lessonsCompleted: Array.isArray(lessonsCompleted) ? lessonsCompleted : [],
      vocalModeEnabled,
      midiInputEnabled,
      midiSelectedDeviceId,
      sessionTargetMinutes,
      sessionMix,
      newSkillNodeId,
      fsrsWeights: Array.isArray(fsrsWeights) ? fsrsWeights : null,
      fsrsWeightsByTrack: fsrsWeightsByTrack !== null && typeof fsrsWeightsByTrack === "object"
        ? fsrsWeightsByTrack
        : null,
      masteryPreset: (["standard", "intensive", "accelerated"] as const).includes(masteryPreset)
        ? masteryPreset
        : "standard",
      cadenceTempo: (["slow", "medium", "fast"] as const).includes(cadenceTempo)
        ? cadenceTempo
        : "slow",
      targetRetention: typeof targetRetention === "number" && targetRetention > 0 && targetRetention < 1
        ? targetRetention
        : 0.9,
      showNextReviewHint: typeof showNextReviewHint === "boolean" ? showNextReviewHint : false,
      confidenceSelfReport: typeof confidenceSelfReport === "boolean" ? confidenceSelfReport : false,
      listenAutoAdvance: typeof listenAutoAdvance === "boolean" ? listenAutoAdvance : false,
      activeSavedPlanId,
      enabledInstrumentTracks: Array.isArray(enabledInstrumentTracks) ? enabledInstrumentTracks : [],
      loaded: true,
    });
  },

  setInstrument: async (instrument) => {
    set({ instrument });
    await setPreference("instrument", instrument);
  },
  setShowLetterNames: async (showLetterNames) => {
    set({ showLetterNames });
    await setPreference("showLetterNames", showLetterNames);
  },
  setIncludeLeadingTone: async (includeLeadingTone) => {
    set({ includeLeadingTone });
    await setPreference("includeLeadingTone", includeLeadingTone);
  },
  setOnboardingComplete: async (onboardingComplete) => {
    set({ onboardingComplete });
    await setPreference("onboardingComplete", onboardingComplete);
  },
  setUISoundsEnabled: async (uiSoundsEnabled) => {
    set({ uiSoundsEnabled });
    await setPreference("uiSoundsEnabled", uiSoundsEnabled);
  },
  setUnlockEverything: async (unlockEverything) => {
    set({ unlockEverything });
    await setPreference("unlockEverything", unlockEverything);
  },
  setPlacementCompleted: async (placementCompleted) => {
    set({ placementCompleted });
    await setPreference("placementCompleted", placementCompleted);
  },
  setGuitarPlacementCompleted: async (guitarPlacementCompleted) => {
    set({ guitarPlacementCompleted });
    await setPreference("guitarPlacementCompleted", guitarPlacementCompleted);
  },
  setGuitarPlacementSeed: async (guitarPlacementSeed) => {
    set({ guitarPlacementSeed });
    await setPreference("guitarPlacementSeed", guitarPlacementSeed);
  },
  setPlacementSeed: async (placementSeed) => {
    set({ placementSeed });
    await setPreference("placementSeed", placementSeed);
  },
  markLessonComplete: async (nodeId) => {
    const current = get().lessonsCompleted;
    if (current.includes(nodeId)) return;
    // Keep the array sorted so the persisted form is stable across runs
    // — otherwise export diffs and equality checks would churn on order.
    const next = [...current, nodeId].sort();
    set({ lessonsCompleted: next });
    await setPreference("lessonsCompleted", next);
  },
  isLessonCompleted: (nodeId) => get().lessonsCompleted.includes(nodeId),
  setVocalModeEnabled: async (vocalModeEnabled) => {
    set({ vocalModeEnabled });
    await setPreference("vocalModeEnabled", vocalModeEnabled);
  },
  setMidiInputEnabled: async (midiInputEnabled) => {
    set({ midiInputEnabled });
    await setPreference("midiInputEnabled", midiInputEnabled);
  },
  setMidiSelectedDeviceId: async (midiSelectedDeviceId) => {
    set({ midiSelectedDeviceId });
    await setPreference("midiSelectedDeviceId", midiSelectedDeviceId);
  },
  setSessionTargetMinutes: async (sessionTargetMinutes) => {
    set({ sessionTargetMinutes });
    await setPreference("sessionTargetMinutes", sessionTargetMinutes);
  },
  setSessionMix: async (sessionMix) => {
    set({ sessionMix });
    await setPreference("sessionMix", sessionMix);
  },
  setNewSkillNodeId: async (newSkillNodeId) => {
    set({ newSkillNodeId });
    await setPreference("newSkillNodeId", newSkillNodeId);
  },
  setFsrsWeights: async (fsrsWeights) => {
    set({ fsrsWeights });
    await setPreference("fsrsWeights", fsrsWeights);
  },
  setFsrsWeightsByTrack: async (fsrsWeightsByTrack) => {
    set({ fsrsWeightsByTrack });
    await setPreference("fsrsWeightsByTrack", fsrsWeightsByTrack);
  },
  setMasteryPreset: async (masteryPreset) => {
    set({ masteryPreset });
    await setPreference("masteryPreset", masteryPreset);
  },
  setCadenceTempo: async (cadenceTempo) => {
    set({ cadenceTempo });
    await setPreference("cadenceTempo", cadenceTempo);
  },
  setTargetRetention: async (targetRetention) => {
    set({ targetRetention });
    await setPreference("targetRetention", targetRetention);
  },
  setShowNextReviewHint: async (showNextReviewHint) => {
    set({ showNextReviewHint });
    await setPreference("showNextReviewHint", showNextReviewHint);
  },
  setConfidenceSelfReport: async (confidenceSelfReport) => {
    set({ confidenceSelfReport });
    await setPreference("confidenceSelfReport", confidenceSelfReport);
  },
  setListenAutoAdvance: async (listenAutoAdvance) => {
    set({ listenAutoAdvance });
    await setPreference("listenAutoAdvance", listenAutoAdvance);
  },
  setEnabledInstrumentTracks: async (enabledInstrumentTracks) => {
    set({ enabledInstrumentTracks });
    await setPreference("enabledInstrumentTracks", enabledInstrumentTracks);
  },
  setActiveSavedPlanId: async (activeSavedPlanId) => {
    set({ activeSavedPlanId });
    await setPreference("activeSavedPlanId", activeSavedPlanId);
  },
}));

/* ─── Saved plans store ──────────────────────────────────────────────── */

interface SavedPlansState {
  plans: SavedPlanRecord[];
  loaded: boolean;
  hydrate: () => Promise<void>;
  savePlan: (plan: SavedPlanRecord) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
}

export const useSavedPlansStore = create<SavedPlansState>((set, get) => ({
  plans: [],
  loaded: false,

  hydrate: async () => {
    const plans = await loadUserPlans();
    set({ plans, loaded: true });
  },

  savePlan: async (plan) => {
    await savePlan(plan);
    const existing = get().plans.findIndex((p) => p.id === plan.id);
    if (existing >= 0) {
      set({ plans: get().plans.map((p) => (p.id === plan.id ? plan : p)) });
    } else {
      set({ plans: [...get().plans, plan] });
    }
  },

  deletePlan: async (id) => {
    await deletePlan(id);
    set({ plans: get().plans.filter((p) => p.id !== id) });
  },
}));
